/**
 * Finish-step (review + checkout) specific colour / gradient literals.
 *
 * Ported from the standalone Configurator's `features/finish/*` +
 * `components/payment/*` (Tailwind classes + inline gradients). These are the
 * literals that ONLY the Finish step / Payment flow use and therefore don't
 * belong in the shared `configurator-tokens.ts`. Anything already covered by the
 * base palette re-uses `CFG` rather than re-declaring a hex.
 *
 * Rule for this area: no raw colour/gradient literal lives in a component file —
 * markup pulls every colour from a token. This file is the single home for the
 * Finish step's own literals.
 */

import { themed } from '../../../../theme/themeRegistry';
import { CFG } from '../../../../theme/configurator-tokens';

export const FINISH = themed('cfgFinish', {
  // Device-preview stage screen backdrops (behind the iframe cut-outs).
  macScreenBg: '#0c0c10', // MacbookFrame screen fill
  phoneScreenBg: '#000000', // PhoneFrame screen fill

  // "Live Preview" pill (header).
  pillBorder: CFG.hairlineStrong, // border-white/15
  pillHover: CFG.wash, // hover:bg-white/5

  // Billing rows.
  rowLabel: CFG.textDim, // text-base-300
  rowSublabelMuted: CFG.textMuted, // default sublabel grey
  rowSublabelSuccess: CFG.success, // "Cancel anytime…" green (#17c964)
  rowNote: CFG.foreground, // note inherits white at 50% opacity

  // Terms & footer copy.
  termsText: 'rgba(255, 255, 255, 0.5)', // text-white/50 underline link

  // Payment pane shell.
  paneBg: '#0d0c12', // the overlay pane backdrop
  paneDivider: CFG.hairline, // border-white/10 (order-total rule)

  // Payment chrome.
  inputBorder: CFG.base100, // border-base-100 (#27272a)
  inputBorderFocus: 'rgba(255, 255, 255, 0.4)', // focus:border-white/40
  inputText: '#ecedee',
  inputPlaceholder: CFG.base500, // placeholder:text-base-500
  payButtonText: '#000000',
  errorText: '#f87171', // text-red-400

  // Checkout error states — the friendly "couldn't start checkout" screen and the
  // inline "payment declined" alert that replace the old bare red sentences.
  errorIconStroke: CFG.danger, // #f04438 — warning glyph
  errorIconBg: 'rgba(240, 68, 56, 0.12)', // soft red disc behind the glyph
  errorIconBorder: 'rgba(240, 68, 56, 0.30)', // ring around the disc
  errorHeading: CFG.foreground, // #ffffff — error title
  errorBody: CFG.textMuted, // #a1a1aa — muted explanation copy
  errorSupportLead: CFG.base500, // #71717a — "still stuck?" lead
  errorAlertBg: 'rgba(240, 68, 56, 0.08)', // inline declined-payment banner tint
  errorAlertBorder: 'rgba(240, 68, 56, 0.28)', // inline banner border

  // Demo payment-method placeholders.
  methodRowBg: '#18181b',
  methodRowBorder: CFG.base100,
  methodLabel: CFG.textDim, // text-base-300
  methodIconStroke: CFG.textMuted, // #a1a1aa — card outline
  expressCardBg: '#17181c',
  expressLinkBg: CFG.successAlt, // #00d66f
  expressAmazonBg: CFG.warning, // #ffd814
  weChatGreen: CFG.successDeep, // #1aad19
  demoNote: CFG.base500,

  // Back arrow (payment pane header).
  backIconStroke: CFG.base50, // #fafafa

  // Payment-success screen.
  successBg: '#08070b',
  successPaneBg: CFG.cardDeep, // #100f13 (right device column)
  successCopyMuted: '#e7e5e4',
  successCloseHint: 'rgba(255, 255, 255, 0.7)', // text-white/70
  // Mobile-only "Open Email App" CTA (white pill, black label).
  successButtonBg: '#ffffff',
  successButtonText: '#000000',

  // Live-preview overlay popup drop shadow.
  previewPopupShadow: '0 40px 120px -20px rgba(0, 0, 0, 0.8)',
} as const);

export const FINISH_GRADIENTS = themed('cfgFinishGradients', {
  /** "Payment successful" heading text fill. */
  successHeading: 'linear-gradient(90deg, #AEA6F1 0%, #CEC1CC 50%, #DA9C74 100%)',
  /** Desktop success-screen decorative bloom. */
  successBloomDesktop:
    'radial-gradient(60% 50% at 12% 30%, rgba(120,40,120,0.22) 0%, rgba(8,7,11,0) 60%)',
  /** Mobile success-screen vignette layered over the colourful bloom photo:
      clear centre → dark edges (matches the Figma radial). */
  successVignetteMobile: 'radial-gradient(ellipse at 50% 50%, rgba(8,8,9,0) 0%, rgba(8,8,9,1) 100%)',
  /** Live-preview overlay backdrop scrim (two stacked dark washes). */
  previewBackdrop:
    'linear-gradient(90deg, rgba(0,0,0,0.48) 0%, rgba(0,0,0,0.48) 100%),' +
    'linear-gradient(90deg, rgba(3,3,6,0.82) 0%, rgba(3,3,6,0.82) 100%)',
} as const);

// Live-preview popup browser-chrome bar — theme-stable near-white in BOTH themes
// (the popup is a fixed browser frame; the dark-on-light logo + device switcher in
// the bar must read regardless of theme). NOT theme-wrapped, so it never flips.
export const FINISH_PREVIEW = { topBar: '#fcfcfc' } as const;
